/**
 * Regras do cartão "Seu livro em mãos" da tela Início (aprovado em 02/11/2026, protótipo
 * "Início com mais vida"). Funções puras: recebem a lista de empréstimos da pessoa e a data de hoje.
 */

/** O que o cartão precisa de cada empréstimo (o `EmprestimoDetalhado` do serviço serve). */
export interface EmprestimoParaLembrete {
  status: string;
  livro_titulo: string;
  /** AAAA-MM-DD; pode faltar. */
  data_devolucao_prevista: string | null;
  created_at: string;
}

export interface LivroEmMaos {
  titulo: string;
  /** AAAA-MM-DD, ou null se o empréstimo não tem prazo. */
  prazo: string | null;
  /** Dias até o prazo (0 = hoje, negativo = passou); null sem prazo. */
  dias: number | null;
  /** Quantos outros livros a pessoa também tem em andamento. */
  outros: number;
}

/** Dias corridos de `de` até `ate` (ambos AAAA-MM-DD), sem depender de fuso nem de horário de verão. */
export function diasEntreDatas(de: string, ate: string): number {
  const utc = (iso: string) => {
    const [ano = 0, mes = 1, dia = 1] = iso.split("-").map(Number);
    return Date.UTC(ano, mes - 1, dia);
  };
  return Math.round((utc(ate) - utc(de)) / 86_400_000);
}

/** "07/11" a partir de AAAA-MM-DD. */
export function diaMes(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

/**
 * O livro que mais precisa de atenção: dos que a pessoa tem em mãos (Em aberto ou Atrasado),
 * o de prazo mais próximo (ou já vencido). Sem nenhum em mãos, devolve null e o cartão não aparece.
 */
export function escolherLivroEmMaos(
  lista: EmprestimoParaLembrete[],
  hoje: string,
): LivroEmMaos | null {
  const emMaos = lista
    .filter((e) => e.status === "EM_ABERTO" || e.status === "ATRASADO")
    .map((e) => {
      const prazo = e.data_devolucao_prevista ? e.data_devolucao_prevista.slice(0, 10) : null;
      return { e, prazo, dias: prazo ? diasEntreDatas(hoje, prazo) : null };
    });
  emMaos.sort((a, b) => {
    // Sem prazo vai para o fim; empate fica com o mais recente.
    if (a.dias === null && b.dias !== null) return 1;
    if (b.dias === null && a.dias !== null) return -1;
    return (a.dias ?? 0) - (b.dias ?? 0) || b.e.created_at.localeCompare(a.e.created_at);
  });
  const primeiro = emMaos[0];
  if (!primeiro) return null;
  return {
    titulo: primeiro.e.livro_titulo,
    prazo: primeiro.prazo,
    dias: primeiro.dias,
    outros: emMaos.length - 1,
  };
}

/** Linha do prazo: "Devolução até 07/11 · faltam 5 dias", "Devolução amanhã, 03/11"... */
export function textoPrazo(dias: number | null, prazo: string | null): string {
  if (dias === null || prazo === null) return "Em andamento";
  const dm = diaMes(prazo);
  if (dias > 1) return `Devolução até ${dm} · faltam ${dias} dias`;
  if (dias === 1) return `Devolução amanhã, ${dm}`;
  if (dias === 0) return `Devolução hoje, ${dm}`;
  const atraso = -dias;
  return `O prazo passou em ${dm} (há ${atraso} ${atraso === 1 ? "dia" : "dias"})`;
}

/** Frase curta de incentivo, de acordo com o prazo. */
export function textoIncentivo(dias: number | null): string | null {
  if (dias === null) return null;
  if (dias < 0) return "Quando puder, devolva na Biblioteca.";
  if (dias <= 1) return "Não esqueça de levar o livro para devolver.";
  if (dias <= 7) return "Que tal ler um pouquinho hoje?";
  return "Boa leitura!";
}

/** "+ 1 outro livro em andamento". Vazio quando não há outros. */
export function textoOutros(outros: number): string | null {
  if (outros <= 0) return null;
  return `+ ${outros} ${outros === 1 ? "outro livro" : "outros livros"} em andamento`;
}
